import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EMERGENCY_CONFIRMATION,
  EMERGENCY_DRILL_CONFIRMATION,
  type AudiencePreview,
  type AudienceSpec,
  type EmergencyTemplate,
  type EmergencyTemplateList,
  type Incident,
  type IncidentBoard,
  type IncidentList,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { cn, useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Empty,
  Field,
  Grid,
  Input,
  Modal,
  Note,
  PageHead,
  Sect,
  Select,
  Stack,
  Stats,
  Textarea,
  useAdminToast,
} from '../admin-kit';
import { AudiencePicker } from '../communication/audience-picker';
import { sendBlock } from './send-block';
import { LoadError, Loading, useShortWhen } from '../werkvloer';
import { useSession } from '../../shared/session';
import { formatTime } from '../../shared/format';
import { audienceIncomplete } from '../../shared/audience-preview';

/** Hoe vaak het overzicht zich verversen laat zolang er iets loopt. */
const BOARD_INTERVAL_MS = 5_000;

type Mode = '' | 'drill' | 'real';

/**
 * Het noodkanaal in het beheer (Stap 35; herontworpen in BHR-6, frame W1).
 *
 * Gebouwd om onder stress te werken: een rood vlak dat zegt wanneer je dit gebruikt, dan de
 * genummerde stappen, en rechts wat er eerder gebeurde en de sjablonen. **Loopt er iets**, dan
 * staat links het live overzicht in plaats van het formulier.
 *
 * Dat het overzicht de startknop verdringt, is met opzet. Tijdens een ontruiming is er maar één
 * vraag — wie staat er nog binnen — en een scherm dat daarnaast een formulier toont om een tweede
 * melding te starten, nodigt uit tot precies de verwarring die je dan niet wil.
 *
 * Er is geen websocket: het overzicht vraagt zichzelf elke vijf seconden opnieuw op. Stap 30
 * (realtime) is vervallen, en voor een lijst van enkele honderden rijen is pollen eerlijker dan een
 * halve realtimelaag. Genoteerd in `docs/product/demo-schuld.md`.
 */
export function EmergencyPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const adminToast = useAdminToast();

  const incidents = useQuery({
    queryKey: ['emergency', 'incidents'],
    queryFn: async (): Promise<Incident[]> => {
      const { data, error } = await api.GET('/api/v1/emergency/incidents', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as IncidentList).items;
    },
    refetchInterval: BOARD_INTERVAL_MS,
  });

  const active = incidents.data?.find((incident) => incident.status === 'active') ?? null;
  const past = (incidents.data ?? []).filter((incident) => incident.status === 'closed');

  return (
    <AdminPage>
      <PageHead title={t('emergency.title')} sub={t('beheer.werkvloer.emergency.sub')} />

      <Card>
        <CardPad>
          <Note
            tone="danger"
            icon={<AdminIcon name="siren" size={18} />}
            lead={t('beheer.werkvloer.emergency.warnLead')}
          >
            {t('beheer.werkvloer.emergency.warnRest')}
          </Note>
        </CardPad>
      </Card>

      <Grid variant="side">
        {incidents.isPending ? (
          <Card>
            <Loading />
          </Card>
        ) : incidents.isError ? (
          <Card>
            <LoadError onRetry={() => void incidents.refetch()} error={incidents.error} />
          </Card>
        ) : active ? (
          <ActiveIncident
            incident={active}
            onClosed={() => adminToast.show(t('emergency.closed'))}
          />
        ) : (
          <StartPanel onStarted={() => adminToast.show(t('emergency.started'))} />
        )}

        <Stack>
          <HistoryCard past={past} />
          <TemplatesCard onSaved={() => adminToast.show(t('emergency.templateSaved'))} />
        </Stack>
      </Grid>
      {adminToast.element}
    </AdminPage>
  );
}

/**
 * De noodmelding versturen, in genummerde stappen.
 *
 * Twee dingen maken hem moeilijk per ongeluk te gebruiken: of het een oefening is, is een keuze
 * zonder standaardwaarde, en in het bevestigingsvenster moet het bevestigingswoord **getypt**
 * worden. Een dialoog met "weet je het zeker" wordt weggeklikt zonder gelezen te worden; een woord
 * typen niet.
 */
function StartPanel({ onStarted }: { onStarted: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');
  const [audience, setAudience] = useState<AudienceSpec>({ type: 'all' });
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [mode, setMode] = useState<Mode>('');
  const [confirming, setConfirming] = useState(false);
  const [confirmation, setConfirmation] = useState('');

  const templates = useTemplates();
  const template = (templates.data ?? []).find((candidate) => candidate.id === templateId) ?? null;

  const expected = mode === 'drill' ? EMERGENCY_DRILL_CONFIRMATION : EMERGENCY_CONFIRMATION;

  const start = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/emergency/incidents', {
        body: {
          templateId: templateId || null,
          title: templateId ? undefined : title,
          instruction: templateId ? undefined : instruction,
          audience,
          siteId: null,
          isDrill: mode === 'drill',
          confirmation,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emergency'] });
      setConfirmation('');
      setConfirming(false);
      setMode('');
      onStarted();
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  // De knop zegt wat er gebeurt en voor hoeveel mensen; zolang de telling loopt, zonder getal.
  const count = preview?.count;
  const sendLabel =
    mode === 'drill'
      ? count !== undefined
        ? t('beheer.werkvloer.emergency.sendDrillTo', { count })
        : t('emergency.sendDrill')
      : count !== undefined
        ? t('beheer.werkvloer.emergency.sendRealTo', { count })
        : t('emergency.send');

  const ready = confirmation.trim().toUpperCase() === expected;
  // "Kiezen" zonder keuze bereikt niemand, ook al loopt het aantal nog achter (hertest 30 okt.).
  const block = sendBlock(mode, audienceIncomplete(audience) ? 0 : count);

  return (
    <Card testId="emergency-start">
      <CardHead title={t('beheer.werkvloer.emergency.startTitle')} />
      <CardPad>
        <ModeChoice mode={mode} onChange={setMode} />

        <Field
          label={`2 · ${t('emergency.templateLabel')}`}
          optional
          hint={t('emergency.templateHint')}
        >
          {(props) => (
            <Select
              {...props}
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
              data-testid="emergency-template"
            >
              <option value="">{t('emergency.templateOwnText')}</option>
              {(templates.data ?? []).map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.title}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* Met een sjabloon staat de tekst van het sjabloon er, alleen te lezen: dat is wat de
            server verstuurt. */}
        <Field label={`3 · ${t('emergency.titleLabel')}`}>
          {(props) => (
            <Input
              {...props}
              value={template ? template.title : title}
              readOnly={template !== null}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={120}
              data-testid="emergency-title-input"
            />
          )}
        </Field>
        <Field label={`4 · ${t('emergency.instructionLabel')}`}>
          {(props) => (
            <Textarea
              {...props}
              value={template ? template.instruction : instruction}
              readOnly={template !== null}
              onChange={(event) => setInstruction(event.target.value)}
              maxLength={500}
              data-testid="emergency-instruction-input"
            />
          )}
        </Field>

        <AudiencePicker value={audience} onChange={setAudience} onPreview={setPreview} />

        <div className="a-actions wv-sep">
          <Btn
            size="lg"
            variant={mode === 'real' ? 'danger' : 'primary'}
            icon={<AdminIcon name="siren" size={18} />}
            disabled={block !== null}
            onClick={() => {
              setConfirmation('');
              setConfirming(true);
            }}
            testId="emergency-send"
          >
            {sendLabel}
          </Btn>
        </div>
        {block === 'chooseMode' ? (
          <div className="a-hint">{t('beheer.werkvloer.emergency.chooseFirst')}</div>
        ) : null}
        {block === 'noRecipients' ? (
          <div className="a-hint" data-testid="emergency-no-recipients">
            {t('beheer.werkvloer.emergency.noRecipients')}
          </div>
        ) : null}
      </CardPad>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={sendLabel}
        testId="emergency-confirm-dialog"
        footer={
          <>
            <Btn variant="sec" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant={mode === 'real' ? 'danger' : 'primary'}
              disabled={!ready || start.isPending}
              onClick={() => start.mutate()}
              testId="emergency-confirm-send"
            >
              {sendLabel}
            </Btn>
          </>
        }
      >
        <Note tone={mode === 'real' ? 'danger' : 'info'} lead={template?.title ?? title}>
          {preview ? preview.description : null}
        </Note>
        <Field
          label={t('emergency.confirmLabel', { word: expected })}
          hint={t('emergency.confirmHint')}
        >
          {(props) => (
            <Input
              {...props}
              value={confirmation}
              autoComplete="off"
              onChange={(event) => setConfirmation(event.target.value)}
              data-testid="emergency-confirm"
            />
          )}
        </Field>
      </Modal>
    </Card>
  );
}

/**
 * Stap 1: echt of een oefening, als twee grote vakken. Geen standaardwaarde: een vergeten keuze
 * die "echt" betekent is de verkeerde kant van die fout, en een vergeten keuze die "oefening"
 * betekent nog erger.
 */
function ModeChoice({ mode, onChange }: { mode: Mode; onChange: (mode: Mode) => void }) {
  const { t } = useTranslation();
  const labelId = 'emergency-mode-label';

  // Pijltjes wisselen tussen de twee vakken, zoals in elke radiogroep.
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const next: Mode = mode === 'drill' ? 'real' : 'drill';
    onChange(next);
    document.getElementById(`emergency-mode-${next}`)?.focus();
  };

  const option = (value: 'drill' | 'real', title: ReactNode, text: string) => {
    const on = mode === value;
    return (
      <button
        type="button"
        role="radio"
        id={`emergency-mode-${value}`}
        aria-checked={on}
        tabIndex={on || (mode === '' && value === 'drill') ? 0 : -1}
        className={cn('a-emerg-opt', on && 'on', on && value)}
        onClick={() => onChange(value)}
        onKeyDown={onKeyDown}
        data-testid={`emergency-mode-${value}`}
      >
        <span className={cn('a-radio', on && 'on')} style={{ marginTop: 4 }} aria-hidden="true" />
        <span>
          <span
            className={cn('a-emerg-opt-title', value === 'real' && 'wv-emerg-real')}
            style={{ display: 'block' }}
          >
            {title}
          </span>
          <span className="a-muted">{text}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="a-field">
      <div className="a-label" id={labelId}>
        1 · {t('emergency.modeLabel')}
      </div>
      <div className="a-emerg-choice" role="radiogroup" aria-labelledby={labelId}>
        {option('drill', t('emergency.modeDrill'), t('beheer.werkvloer.emergency.drillText'))}
        {option('real', t('emergency.modeReal'), t('beheer.werkvloer.emergency.realText'))}
      </div>
    </div>
  );
}

/**
 * Het live overzicht, **met namen** (ADR-0060).
 *
 * Hulp eerst, dan stil, dan veilig. Dat is de volgorde waarin iemand die een ontruiming leidt
 * ernaar kijkt — niet de volgorde waarin de antwoorden binnenkwamen.
 */
function ActiveIncident({ incident, onClosed }: { incident: Incident; onClosed: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const board = useQuery({
    queryKey: ['emergency', 'board', incident.id],
    queryFn: async (): Promise<IncidentBoard> => {
      const { data, error } = await api.GET('/api/v1/emergency/incidents/{incidentId}', {
        params: { path: { incidentId: incident.id } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as IncidentBoard;
    },
    refetchInterval: BOARD_INTERVAL_MS,
  });

  const close = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/emergency/incidents/{incidentId}/close', {
        params: { path: { incidentId: incident.id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emergency'] });
      onClosed();
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <Card testId="emergency-board">
      <CardHead
        title={
          <>
            {incident.title}{' '}
            <Badge tone={incident.isDrill ? 'info' : 'danger'}>
              {incident.isDrill ? t('emergency.drill') : t('beheer.werkvloer.emergency.real')}
            </Badge>
          </>
        }
        actions={
          <Btn
            variant="danger-sec"
            size="sm"
            disabled={close.isPending}
            onClick={() => close.mutate()}
            testId="emergency-close"
          >
            {t('emergency.closeAction')}
          </Btn>
        }
      />
      <CardPad>
        <div>
          <div className="wv-text">{incident.instruction}</div>
          <div className="a-meta" style={{ marginTop: 4 }}>
            {t('emergency.startedBy', {
              name: incident.startedByName,
              time: formatTime(incident.startedAt),
            })}{' '}
            · {incident.audienceDescription} ·{' '}
            {t('emergency.recipients', { count: incident.recipientCount })}
          </div>
        </div>

        {board.isPending ? (
          <Loading />
        ) : board.isError ? (
          <LoadError onRetry={() => void board.refetch()} error={board.error} />
        ) : (
          <>
            <Stats
              items={[
                {
                  label: t('beheer.werkvloer.emergency.statHelp'),
                  value: board.data.help.length,
                },
                {
                  label: t('beheer.werkvloer.emergency.statSilent'),
                  value: board.data.silent.length,
                },
                {
                  label: t('beheer.werkvloer.emergency.statSafe'),
                  value: board.data.safe.length,
                },
              ]}
            />
            <PersonGroup
              title={t('emergency.groupHelp', { count: board.data.help.length })}
              testId="emergency-group-help"
              rows={board.data.help.map((response) => ({
                key: response.personId,
                name: response.displayName,
                detail: response.note || formatTime(response.at),
              }))}
              empty={t('emergency.groupHelpEmpty')}
            />
            <PersonGroup
              title={t('emergency.groupSilent', { count: board.data.silent.length })}
              testId="emergency-group-silent"
              rows={board.data.silent.map((person) => ({
                key: person.personId,
                name: person.displayName,
                detail: t('emergency.reminders', { count: person.reminders }),
              }))}
              empty={t('emergency.groupSilentEmpty')}
            />
            <PersonGroup
              title={t('emergency.groupSafe', { count: board.data.safe.length })}
              testId="emergency-group-safe"
              rows={board.data.safe.map((response) => ({
                key: response.personId,
                name: response.displayName,
                detail: formatTime(response.at),
              }))}
              empty={t('emergency.groupSafeEmpty')}
            />
          </>
        )}
      </CardPad>
    </Card>
  );
}

function PersonGroup({
  title,
  rows,
  empty,
  testId,
}: {
  title: string;
  rows: { key: string; name: string; detail: string }[];
  empty: string;
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <Sect title={title}>
        {rows.length === 0 ? (
          <p className="a-muted a-small">{empty}</p>
        ) : (
          <ul className="wv-people">
            {rows.map((row) => (
              <li key={row.key}>
                <span className="a-name">{row.name}</span>
                <span className="a-meta">{row.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </Sect>
    </div>
  );
}

/** "Eerdere meldingen": per melding de uitkomst, zoals een preventieadviseur ze terugzoekt. */
function HistoryCard({ past }: { past: Incident[] }) {
  const { t } = useTranslation();
  const when = useShortWhen();

  return (
    <Card>
      <CardHead title={t('emergency.history')} count={past.length} />
      <CardPad>
        {past.length === 0 ? (
          <Empty
            icon={<AdminIcon name="siren" size={24} />}
            title={t('emergency.historyEmptyTitle')}
            text={t('emergency.historyEmptyHint')}
          />
        ) : (
          past.map((incident) => {
            const minutes = incident.closedAt
              ? Math.max(
                  1,
                  Math.round(
                    (new Date(incident.closedAt).getTime() -
                      new Date(incident.startedAt).getTime()) /
                      60_000,
                  ),
                )
              : null;
            return (
              <div
                key={incident.id}
                className="a-item"
                style={{ padding: 0 }}
                data-testid="emergency-history-row"
              >
                <span className={cn('a-tile', incident.isDrill ? 'green' : 'danger')} aria-hidden>
                  {incident.isDrill ? (
                    <AdminIcon name="shieldCheck" size={20} />
                  ) : (
                    <AdminIcon name="siren" size={20} />
                  )}
                </span>
                <div className="a-item-info">
                  <div className="a-item-top">
                    <Badge tone={incident.isDrill ? 'info' : 'danger'}>
                      {incident.isDrill
                        ? t('emergency.drill')
                        : t('beheer.werkvloer.emergency.real')}
                    </Badge>
                    <span className="a-meta">{when(incident.startedAt)}</span>
                  </div>
                  <div className="a-name">{incident.title}</div>
                  <div className="a-meta">
                    {t('beheer.werkvloer.emergency.outcome', {
                      safe: incident.safeCount,
                      count: incident.recipientCount,
                    })}
                    {minutes !== null
                      ? ` · ${
                          minutes < 120
                            ? t('beheer.werkvloer.emergency.after', { count: minutes })
                            : t('beheer.werkvloer.emergency.afterHours', {
                                count: Math.round(minutes / 60),
                              })
                        }`
                      : ''}
                    {incident.helpCount > 0
                      ? ` · ${t('emergency.helpCount', { count: incident.helpCount })}`
                      : ''}
                    {incident.silentCount > 0
                      ? ` · ${t('beheer.werkvloer.emergency.unreached', { count: incident.silentCount })}`
                      : ''}
                  </div>
                  <div className="a-meta">
                    {incident.audienceDescription} · {incident.startedByName}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardPad>
    </Card>
  );
}

function useTemplates() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['emergency', 'templates'],
    queryFn: async (): Promise<EmergencyTemplate[]> => {
      const { data } = await api.GET('/api/v1/emergency/templates', {});
      return (data as EmergencyTemplateList | undefined)?.items ?? [];
    },
  });
}

/** Sjablonen: de tekst die iemand onder druk niet meer hoeft te bedenken. */
function TemplatesCard({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');

  const templates = useTemplates();

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/emergency/templates', {
        body: { title, instruction },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emergency', 'templates'] });
      setTitle('');
      setInstruction('');
      setOpen(false);
      onSaved();
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const items = templates.data ?? [];

  return (
    <Card>
      <CardHead title={t('emergency.templatesTitle')} count={items.length} />
      <CardPad>
        {items.length === 0 ? (
          <p className="a-muted a-small">{t('emergency.templatesIntro')}</p>
        ) : null}
        {items.map((template) => (
          <div
            key={template.id}
            className="a-item"
            style={{ padding: 0 }}
            data-testid="emergency-template-row"
          >
            <div className="a-item-info">
              <div className="a-name">{template.title}</div>
              <div className="a-meta">{excerpt(template.instruction)}</div>
            </div>
          </div>
        ))}
        <div>
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" />}
            onClick={() => setOpen(true)}
            testId="emergency-template-new"
          >
            {t('beheer.werkvloer.emergency.addTemplate')}
          </Btn>
        </div>
      </CardPad>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('beheer.werkvloer.emergency.addTemplate')}
        sub={t('emergency.templatesIntro')}
        footer={
          <>
            <Btn variant="sec" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              disabled={title.trim() === '' || instruction.trim() === '' || create.isPending}
              onClick={() => create.mutate()}
              testId="emergency-template-save"
            >
              {t('emergency.templateSave')}
            </Btn>
          </>
        }
      >
        <Field label={t('emergency.templateTitleLabel')}>
          {(props) => (
            <Input
              {...props}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={120}
              data-testid="emergency-template-title"
            />
          )}
        </Field>
        <Field label={t('emergency.templateInstructionLabel')}>
          {(props) => (
            <Textarea
              {...props}
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              maxLength={500}
              data-testid="emergency-template-instruction"
            />
          )}
        </Field>
      </Modal>
    </Card>
  );
}

/** Het begin van een instructie, zoals in de mockup: één regel met "…". */
function excerpt(text: string, length = 56): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > length ? `${clean.slice(0, length).trimEnd()}…` : clean;
}
